import { ReceiveWebsocketMessagePayload } from 'redux/actions/chatsActions';
import { isPendingChat } from 'redux/model/chats/pendingChat';
import { ChatMessage, messageStatusEnum } from 'types/chatsType';
import { ChatType } from 'types/stateTypes';
import { getChatPreviewFromWsMessage, getNotificationInfo, getUser, sortChatsByPinAndLastMessage } from 'utils/chats.utils.typed';

const CHAT_DELETED_TEXT = 'удалил(а) чат';

function markOpenDialogDeleted(state: ChatType, dialogId: ChatType['selectedChatId']): ChatType['selectedDialogInfo'] {
  if (dialogId !== state.selectedChatId || !state.selectedDialogInfo) {
    return state.selectedDialogInfo;
  }
  return { ...state.selectedDialogInfo, isDeleted: true };
}

function applyChangeChat(
  state: ChatType,
  payload: ReceiveWebsocketMessagePayload,
  selectedDialogInfo: ChatType['selectedDialogInfo']
): ChatType {
  const { fullMessage } = payload;
  const dialogId = fullMessage.dialog;
  const wsAction = fullMessage.action;
  let updatedSelectedDialogInfo = selectedDialogInfo;
  let updatedChats = state.chats;

  const dialogName = wsAction.name;
  if (dialogName) {
    updatedChats = updatedChats.map((chat) =>
      chat.id === dialogId ? { ...chat, name: dialogName } : chat
    );

    if (dialogId === state.selectedChatId && updatedSelectedDialogInfo) {
      updatedSelectedDialogInfo = { ...updatedSelectedDialogInfo, name: dialogName };
    }
  }

  let updatedChatInfo = state.chatInfo;
  if (state.selectedChatId === dialogId) {
    const actionMessageId = Number(wsAction.messageId);
    const isNewMessage = updatedChatInfo.findIndex((message) => message.id === actionMessageId);
    updatedChatInfo = updatedChatInfo.map((message) =>
      message.id === actionMessageId
        ? {
            ...message,
            files: wsAction.files ?? null,
            text: wsAction.text ?? null,
          }
        : message
    );
    if (isNewMessage === -1) {
      const newMessage: ChatMessage = {
        createdAt: new Date().toISOString(),
        files: [],
        id: fullMessage.id,
        ownerId: null,
        read: messageStatusEnum.sent,
        text: fullMessage.text,
        type: fullMessage.cType,
      };
      updatedChatInfo = updatedChatInfo.concat([newMessage]);
    }
  }

  return { ...state, chatInfo: updatedChatInfo, chats: updatedChats, selectedDialogInfo: updatedSelectedDialogInfo };
}

function applyDeleteMessages(state: ChatType, payload: ReceiveWebsocketMessagePayload): ChatType {
  const { fullMessage } = payload;
  if (fullMessage.dialog !== state.selectedChatId) {
    return state;
  }
  const deletedMessages = fullMessage.action.messages ?? [];
  const updatedChatInfo = state.chatInfo.filter(
    (message) => !deletedMessages.find((messageId) => messageId === message.id)
  );
  return { ...state, chatInfo: updatedChatInfo };
}

function applyIncomingMessage(state: ChatType, payload: ReceiveWebsocketMessagePayload): ChatType {
  const { fullMessage, isUnread } = payload;
  const dialogId = fullMessage.dialog;
  const pendingInterlocutorId = state.selectedDialogInfo?.interlocutor?.userId;
  const shouldBindPending =
    isPendingChat(state) &&
    dialogId != null &&
    (!isUnread || fullMessage.owner === pendingInterlocutorId);
  const selectedChatId = shouldBindPending ? dialogId : state.selectedChatId;
  const isNewUnread = isUnread && dialogId !== selectedChatId;
  const existingChat = state.chats.find((chat) => chat.id === dialogId);
  const updatedChats = sortChatsByPinAndLastMessage(
    existingChat
      ? state.chats.map((chat) => {
          if (chat.id !== dialogId) {
            return chat;
          }
          return {
            ...chat,
            messageId: fullMessage.id,
            preview: getChatPreviewFromWsMessage(fullMessage, chat),
            unreadMessageCount: isNewUnread ? chat.unreadMessageCount + 1 : chat.unreadMessageCount,
          };
        })
      : [...state.chats]
  );

  let newMessageInfo;
  if (isNewUnread && existingChat && dialogId != null) {
    const user = getUser({ dialog: existingChat, userId: fullMessage.owner });
    newMessageInfo = getNotificationInfo({
      dialogId,
      messageId: fullMessage.id,
      user,
    });
  }

  let messages = state.chatInfo;
  if (dialogId === selectedChatId && fullMessage.id) {
    const isRemovedChat = fullMessage.action?.type === 'removeChat';
    const newMessage: ChatMessage = isRemovedChat
      ? {
          createdAt: new Date().toISOString(),
          files: [],
          id: fullMessage.id,
          ownerId: null,
          poll: fullMessage.poll,
          read: messageStatusEnum.sent,
          text: fullMessage.text,
          type: fullMessage.cType,
        }
      : {
          ansPreview: fullMessage.ansPreview,
          answered: fullMessage.answered,
          createdAt: new Date().toISOString(),
          files: fullMessage.files ?? null,
          forwarded: fullMessage.forwarded,
          fwdPreview: fullMessage.fwdPreview,
          id: fullMessage.id,
          ownerId: fullMessage.owner,
          poll: fullMessage.poll,
          read: messageStatusEnum.sent,
          text: fullMessage.text,
          type: fullMessage.cType,
        };
    messages = state.chatInfo.concat([newMessage]);
  }

  return {
    ...state,
    chatInfo: messages,
    chats: updatedChats,
    ...(shouldBindPending && { selectedChatId }),
    ...(!!newMessageInfo && { newNotificationInfo: newMessageInfo }),
  };
}

export function applyReceiveWebsocketMessage(state: ChatType, payload: ReceiveWebsocketMessagePayload): ChatType {
  const { fullMessage } = payload;
  const dialogId = fullMessage.dialog;
  let selectedDialogInfo = state.selectedDialogInfo;

  if (fullMessage.text?.includes(CHAT_DELETED_TEXT) && !fullMessage.owner) {
    selectedDialogInfo = markOpenDialogDeleted(state, dialogId);
  }

  const actionType = fullMessage.action?.type;
  if (actionType === 'changeChat') {
    return applyChangeChat(state, payload, selectedDialogInfo);
  }
  if (actionType === 'delete') {
    return applyDeleteMessages({ ...state, selectedDialogInfo }, payload);
  }
  if (actionType === 'read') {
    return { ...state, selectedDialogInfo };
  }
  return applyIncomingMessage({ ...state, selectedDialogInfo }, payload);
}
